import { absUrl } from "@/lib/url";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { MonthGrid, CalendarLegend } from "@/components/calendar/MonthGrid";
import { MiniMonthLegend } from "@/components/calendar/MiniMonthLegend";
import { PrayerCrossLinkCard } from "@/components/calendar/PrayerCrossLinkCard";
import { AdSlot } from "@/components/AdSlot";
import { PdfExportButton } from "@/components/PdfExportButton";
import {
  parseISO,
  dayOfWeek,
  DAY_NAMES_SHORT_ID,
  calendarMonthPath,
  calendarYearPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import { getHolidaysForYear, isEstimatedYear } from "@/data/holidays";
import { getAllHariPentingForYear } from "@/data/hari-penting";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName, translateHariPenting } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";
import { LongWeekendTable } from "@/components/pages/PseoExtras";
import { buildYearSemantics } from "@/lib/calendar/semantics";
import { SemanticSection } from "@/components/seo/SemanticSection";
import { calendarEntities } from "@/lib/seo/entities";

export function YearPage({ y, locale }: { y: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <YearBody y={y} />
    </SiteShell>
  );
}

function YearBody({ y }: { y: number }) {
  const { t, href, locale } = useLocale();
  const holidays = getHolidaysForYear(y);
  const liburNasional = holidays.filter((h) => h.type === "libur");
  const cutiBersama = holidays.filter((h) => h.type === "cuti-bersama");
  const hariPenting = getAllHariPentingForYear(y);
  const daysShort = locale === "en"
    ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    : DAY_NAMES_SHORT_ID;
  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t.common.home, item: absUrl(href("/")) },
      { "@type": "ListItem", position: 2, name: t.common.calendar, item: absUrl(href("/kalender/")) },
      { "@type": "ListItem", position: 3, name: `${y}`, item: absUrl(href(calendarYearPath(y))) },
    ],
  };
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${t.common.calendar} ${y}`,
    inLanguage: locale === "en" ? "en" : "id-ID",
    isPartOf: { "@type": "WebSite", name: "Hari.co.id", url: "https://hari.co.id/" },
    about: calendarEntities(),
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: liburNasional.length + cutiBersama.length,
      itemListElement: [...liburNasional, ...cutiBersama].slice(0, 50).map((h, i) => {
        const p = parseISO(h.date);
        const url = p ? `https://hari.co.id${calendarDatePath(p.y, p.m, p.d)}` : "https://hari.co.id/";
        return { "@type": "ListItem", position: i + 1, name: h.name, url, item: url };
      }),
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionLd) }} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link>
        {" / "}
        <Link to={href("/kalender/") as never} className="hover:text-primary">{t.common.calendar}</Link>
        {" / "}{y}
      </nav>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl tracking-tight md:text-5xl">{t.common.calendar} {y}</h1>
          <p className="mt-1 text-muted-foreground">
            {t.common.summaryHoliday(liburNasional.length, cutiBersama.length)}
            {isEstimatedYear(y) && <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs">{t.common.estimatedData}</span>}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <Link to={href(`/tanggal-merah/${y}`) as never} className="rounded-md bg-red-date/10 px-2.5 py-1 text-red-date hover:bg-red-date/20">{t.common.redDates} {y}</Link>
            <Link to={href(`/libur-nasional/${y}`) as never} className="rounded-md bg-muted px-2.5 py-1 hover:bg-accent">{t.common.nationalHolidays} {y}</Link>
            <Link to={href(`/cuti-bersama/${y}`) as never} className="rounded-md bg-muted px-2.5 py-1 hover:bg-accent">{t.common.jointLeave} {y}</Link>
          </div>
        </div>
        <FeaturedSvg
          variant="year"
          title={`${t.common.calendar} ${y}`}
          subtitle={t.common.summaryHoliday(liburNasional.length, cutiBersama.length)}
          badge={String(y)}
          locale={locale}
          className="hidden md:block md:w-72"
        />
        <div className="flex items-center gap-3">
          <CalendarLegend />
          <PdfExportButton mode="year" year={y} allowSwitch />
        </div>
      </header>
      <div className="space-y-6">
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <div key={m} className="space-y-1">
              <Link to={href(calendarMonthPath(y, m)) as never} className="block hover:opacity-90">
                <MonthGrid year={y} month={m} compact={false} showSubLabels={false} align="auto" />
              </Link>
              <MiniMonthLegend year={y} month={m} />
            </div>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 font-semibold">{t.common.nationalHolidays} {y}</h2>
            <ul className="space-y-1.5 text-sm">
              {liburNasional.map((h) => {
                const p = parseISO(h.date)!;
                const dow = dayOfWeek(p.y, p.m, p.d);
                return (
                  <li key={`${h.date}-${h.name}`}>
                    <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
                      <span className="text-muted-foreground">{daysShort[dow]}, </span>
                      <span className="text-red-date font-medium">{p.d} {t.common.months[p.m - 1]}</span> - {translateHolidayName(h.name, locale)}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link to={href(`/libur-nasional/${y}`) as never} className="mt-2 inline-block text-xs text-primary hover:underline">
              {t.common.seeDetail} →
            </Link>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 font-semibold">{t.common.jointLeave} {y}</h2>
            {cutiBersama.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {cutiBersama.map((h) => {
                  const p = parseISO(h.date)!;
                  const dow = dayOfWeek(p.y, p.m, p.d);
                  return (
                    <li key={`${h.date}-${h.name}`}>
                      <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
                        <span className="text-muted-foreground">{daysShort[dow]}, </span>
                        <span className="text-cuti-bersama font-medium">{p.d} {t.common.months[p.m - 1]}</span> - {translateHolidayName(h.name, locale)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 font-semibold">{t.common.importantDays} {y}</h2>
            {hariPenting.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <ul className="max-h-96 space-y-1.5 overflow-y-auto pr-1 text-sm">
                {hariPenting.map((h) => {
                  const dow = dayOfWeek(y, h.month, h.day);
                  return (
                    <li key={`${h.monthDay}-${h.name}`} className="flex items-start gap-2">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-hari-penting" />
                      <Link to={href(calendarDatePath(y, h.month, h.day)) as never} className="hover:text-primary">
                        <span className="text-muted-foreground">{daysShort[dow]}, </span>
                        <span className="font-medium">{h.day} {t.common.months[h.month - 1]}</span>
                        <span className="text-muted-foreground"> - </span>
                        <span>{translateHariPenting(h.name, locale)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <AdSlot size="rectangle" position="sidebar" />
      </div>
      <LongWeekendTable year={y} holidays={holidays} />
      {locale === "id" && (
        <div className="mt-6">
          <PrayerCrossLinkCard mode="year" y={y} m={1} />
        </div>
      )}
      {locale === "id" && (
        <div className="mt-6">
          <SemanticSection
            block={buildYearSemantics(y)}
            heading={`Tentang Kalender ${y}`}
          />
        </div>
      )}
    </>
  );
}
